import React from 'react';
import './Pages.css';

const GDPR: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>GDPR Compliance</h1>
      </div>
      
      <div className="page-content legal-content">
        <p className="last-updated">Last updated: January 2024</p>

        <section className="content-section">
          <h2>Our GDPR & AI Act Commitment</h2>
          <p>
            GET AI PORTRAIT is fully committed to complying with the General Data Protection 
            Regulation (GDPR), the EU AI Act, and protecting the privacy rights of all users, 
            especially EU citizens. We implement privacy-by-design principles and ensure 
            transparent AI processing.
          </p>
        </section>

        <section className="content-section">
          <h2>Your Rights Under GDPR</h2>
          
          <h3>1. Right to Access</h3>
          <p>You can request a copy of all personal data we hold about you.</p>

          <h3>2. Right to Rectification</h3>
          <p>You can request correction of any inaccurate personal data.</p>

          <h3>3. Right to Erasure ("Right to be Forgotten")</h3>
          <p>You can request deletion of your personal data from our systems.</p>

          <h3>4. Right to Data Portability</h3>
          <p>You can request your data in a machine-readable format.</p>

          <h3>5. Right to Object</h3>
          <p>You can object to processing of your personal data for marketing purposes.</p>

          <h3>6. Right to Restrict Processing</h3>
          <p>You can request limitation of processing under certain circumstances.</p>
        </section>

        <section className="content-section">
          <h2>Legal Basis for Processing</h2>
          <p>We process personal data based on:</p>
          <ul>
            <li><strong>Explicit Consent (Article 9):</strong> Required for biometric data processing</li>
            <li><strong>Contract:</strong> Processing is necessary to provide our service</li>
            <li><strong>Legitimate Interest:</strong> For improving our service and user experience</li>
            <li><strong>Legal Obligation:</strong> When required by law</li>
          </ul>
          <p>
            <strong>Special Category Data:</strong> Biometric data (facial features) is processed 
            only with your explicit, informed consent, which can be withdrawn at any time.
          </p>
        </section>

        <section className="content-section">
          <h2>AI-Specific Rights Under GDPR & AI Act</h2>
          <p>
            In addition to standard GDPR rights, you have specific rights regarding AI processing:
          </p>
          <ul>
            <li><strong>Right to Explanation:</strong> Understand how AI processes your data</li>
            <li><strong>Right to Human Review:</strong> Request human oversight of AI decisions</li>
            <li><strong>Right to Object to AI Processing:</strong> Opt-out of AI-based processing</li>
            <li><strong>Right to Transparency:</strong> Clear information about AI system capabilities and limitations</li>
            <li><strong>Right to Non-Discrimination:</strong> Protection against AI bias</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Data Protection Measures</h2>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security audits and assessments</li>
            <li>Limited access controls</li>
            <li>Data minimization practices</li>
            <li>Privacy by design principles</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Data Processing Details</h2>
          <h3>What We Collect</h3>
          <ul>
            <li>Email address (for account creation)</li>
            <li>Photos containing faces (for AI processing)</li>
            <li>Biometric data (facial features for artistic transformation only)</li>
            <li>Usage data (for service improvement)</li>
            <li>Consent records (for compliance documentation)</li>
          </ul>

          <h3>How Long We Keep It</h3>
          <ul>
            <li>Account data: Until account deletion</li>
            <li>Uploaded photos: Maximum 30 days after processing</li>
            <li>Biometric data: Deleted immediately after portrait generation</li>
            <li>Generated portraits: Until you delete them</li>
            <li>Usage data: 12 months</li>
            <li>Consent records: As required by law (typically 3 years)</li>
          </ul>

          <h3>Processing Locations</h3>
          <ul>
            <li>Primary processing: EU data centers</li>
            <li>Backup storage: EU-compliant facilities</li>
            <li>No transfer to countries without adequate protection</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>International Data Transfers</h2>
          <p>
            When we transfer data outside the EU, we ensure adequate protection through:
          </p>
          <ul>
            <li>Standard Contractual Clauses</li>
            <li>Adequacy decisions</li>
            <li>Appropriate safeguards</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Data Breach Procedures</h2>
          <p>
            In the unlikely event of a data breach, we will:
          </p>
          <ul>
            <li>Notify authorities within 72 hours</li>
            <li>Inform affected users without undue delay</li>
            <li>Take immediate steps to mitigate damage</li>
            <li>Document the breach and our response</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Automated Decision-Making & Profiling</h2>
          <p>
            <strong>Transparency about AI Processing:</strong>
          </p>
          <ul>
            <li>We use AI for artistic style transfer, not for decision-making about you</li>
            <li>No automated profiling that produces legal or significant effects</li>
            <li>AI processing is limited to portrait generation</li>
            <li>Human oversight is maintained for content moderation</li>
            <li>You can request human review of any AI-related concern</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Exercising Your Rights</h2>
          <p>
            To exercise any of your GDPR or AI Act rights:
          </p>
          <ol>
            <li>Use in-app privacy settings for immediate actions</li>
            <li>Email our Data Protection Officer for complex requests</li>
            <li>Expect acknowledgment within 48 hours</li>
            <li>Receive full response within 30 days</li>
          </ol>
          <p>
            <strong>Contact Information:</strong><br/>
            Data Protection Officer: <a href="mailto:dpo@dreif-ai.com">dpo@dreif-ai.com</a><br/>
            Privacy Team: <a href="mailto:privacy@dreif-ai.com">privacy@dreif-ai.com</a><br/>
            Response time: Within 30 days (usually faster)<br/>
            No fee for reasonable requests
          </p>
        </section>

        <section className="content-section">
          <h2>Children's Privacy Protection</h2>
          <p>
            <strong>Special protections for minors:</strong>
          </p>
          <ul>
            <li>Users must be 16+ in EU countries (13+ elsewhere)</li>
            <li>We do not process biometric data of known minors</li>
            <li>Parental consent required for users under 18</li>
            <li>Enhanced privacy defaults for younger users</li>
            <li>No marketing to users under 18</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>Supervisory Authority</h2>
          <p>
            You have the right to lodge a complaint with your local data protection authority 
            if you believe we have not complied with GDPR or AI Act requirements.
          </p>
          <p>
            <strong>Lead Supervisory Authority:</strong><br/>
            Berlin Commissioner for Data Protection and Freedom of Information<br/>
            Website: <a href="https://www.datenschutz-berlin.de" target="_blank" rel="noopener noreferrer">www.datenschutz-berlin.de</a>
          </p>
          <p>
            EU residents can also contact their local data protection authority. Find yours at:<br/>
            <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer">European Data Protection Board Members</a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default GDPR;